import React, { createContext, useContext, useState, useEffect } from 'react';
import { siteData } from '../content/siteData';

const DB_KEY = 'saltium_global_production_data';
const CONSENT_KEY = 'saltium_cookie_consent_v2';

interface FullscreenGalleryState {
  images: string[];
  index: number;
}

interface SiteContent {
  logo: string;
  heroVideo: string;
  settings: any;
  contact: any;
  socialLinks?: any;
  localization: Record<string, Record<string, string>>;
  sectionOrder: string[];
  projects: any[];
  technologies: any[];
  team: any[];
  partners: any[];
  platforms: any[];
  services: any[];
}

interface CookieConsentState {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  responded: boolean;
}

interface ContentContextType {
  content: SiteContent;
  updateContent: (updates: Partial<SiteContent>) => void;
  publishContent: () => Promise<boolean>;
  resetToDefaults: () => void;
  lang: string;
  setLang: (lang: string) => void;
  t: (key: string | any) => string;
  isLoading: boolean;
  isTheatreMode: boolean;
  setIsTheatreMode: (active: boolean) => void;
  is3DInspectionActive: boolean;
  setIs3DInspectionActive: (active: boolean) => void;
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
  consent: CookieConsentState;
  updateConsent: (newConsent: Partial<CookieConsentState>) => void;
  fullscreenGallery: FullscreenGalleryState | null;
  setFullscreenGallery: (state: FullscreenGalleryState | null) => void;
}

const INITIAL_CONTENT: SiteContent = {
  logo: 'https://static.saltiumgames.com/resources/saltium-games-logo.webp',
  heroVideo: 'https://static.vanished.game/intro.mp4',
  settings: siteData.settings,
  contact: siteData.contact,
  socialLinks: (siteData as any).socialLinks || {},
  localization: siteData.localization || {},
  sectionOrder: (siteData as any).sectionOrder || ['about', 'services', 'projects', 'partners', 'technologies', 'platforms', 'team'],
  projects: siteData.projects || [],
  technologies: siteData.technologies || [],
  team: siteData.team || [],
  partners: (siteData as any).partners || [],
  platforms: (siteData as any).platforms || [],
  services: siteData.services || []
};

const DEFAULT_CONSENT: CookieConsentState = {
  essential: true,
  analytics: false,
  marketing: false,
  responded: false
};

const ContentContext = createContext<ContentContextType | undefined>(undefined);

export const ContentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem('saltium_lang') || 'en');
  const [content, setContent] = useState<SiteContent>(INITIAL_CONTENT);
  const [isLoading, setIsLoading] = useState(true);
  const [isTheatreMode, setIsTheatreMode] = useState(false);
  const [is3DInspectionActive, setIs3DInspectionActive] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [fullscreenGallery, setFullscreenGallery] = useState<FullscreenGalleryState | null>(null);
  const [consent, setConsent] = useState<CookieConsentState>(() => {
    try {
      const saved = localStorage.getItem(CONSENT_KEY);
      return saved ? JSON.parse(saved) : DEFAULT_CONSENT;
    } catch {
      return DEFAULT_CONSENT;
    }
  });

  useEffect(() => {
    // Google Tag Manager
    // Best practice: Load GTM if any non-essential consent is granted, 
    // and handle specific tag firing within GTM.
    if ((consent.analytics || consent.marketing) && !window.location.host.includes('localhost')) {
      const GTM_ID = 'GTM-M3LXQP7H';

      // Load GTM script
      (function (w: any, d: any, s: string, l: string, i: string) {
        w[l] = w[l] || [];
        w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
        const f = d.getElementsByTagName(s)[0];
        const j = d.createElement(s);
        const dl = l !== 'dataLayer' ? '&l=' + l : '';
        j.async = true;
        j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
        if (f && f.parentNode) {
          f.parentNode.insertBefore(j, f);
        } else {
          d.head.appendChild(j);
        }
      })(window, document, 'script', 'dataLayer', GTM_ID);
    }

    // reCAPTCHA - Specifically under Marketing/Security consent
    if (consent.marketing) {
      // Check if script already exists to avoid duplicates
      if (!document.querySelector('script[src*="recaptcha/api.js"]')) {
        const script = document.createElement('script');
        script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
    }
  }, [consent.analytics, consent.marketing]);

  const updateConsent = (newConsent: Partial<CookieConsentState>) => {
    const updated = { ...consent, ...newConsent, responded: true };
    setConsent(updated);
    localStorage.setItem(CONSENT_KEY, JSON.stringify(updated));
  };

  const loadData = () => {
    try {
      const saved = localStorage.getItem(DB_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setContent(prev => ({
          ...prev,
          ...parsed,
          localization: { ...prev.localization, ...(parsed.localization || {}) }
        }));
      } else {
        setContent(INITIAL_CONTENT);
      }
    } catch (e) {
      console.error("Failed to load local storage data", e);
      setContent(INITIAL_CONTENT);
    }
  };

  useEffect(() => {
    loadData();
    setIsLoading(false);
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === DB_KEY) loadData();
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (content.settings?.ui?.primaryColor) {
      root.style.setProperty('--primary-color', content.settings.ui.primaryColor);
    }
    if (content.settings?.ui?.primaryGlow) {
      root.style.setProperty('--primary-glow', content.settings.ui.primaryGlow);
    }
    localStorage.setItem('saltium_lang', lang);
  }, [content.settings, lang]);

  const updateContent = (updates: Partial<SiteContent>) => {
    setContent(prev => ({ ...prev, ...updates }));
  };

  const publishContent = async (): Promise<boolean> => {
    setIsLoading(true);
    try {
      localStorage.setItem(DB_KEY, JSON.stringify(content));
      window.dispatchEvent(new Event('storage'));
      return true;
    } catch (e) {
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const resetToDefaults = () => {
    if (window.confirm("Ovo će izbrisati sve promjene i vratiti sajt na originalne postavke. Da li ste sigurni?")) {
      localStorage.removeItem(DB_KEY);
      window.location.reload();
    }
  };

  const t = (input: any): string => {
    if (!input) return '';
    const localization = content.localization || INITIAL_CONTENT.localization;

    if (typeof input === 'string') {
      const loc = localization[input];
      if (loc) return loc[lang] || loc['en'] || input;
      return input;
    }
    if (input && typeof input === 'object') {
      return input[lang] || input['en'] || Object.values(input)[0] as string || '';
    }
    return String(input);
  };

  return (
    <ContentContext.Provider value={{
      content, updateContent, publishContent, resetToDefaults,
      lang, setLang, t, isLoading, isTheatreMode, setIsTheatreMode,
      is3DInspectionActive, setIs3DInspectionActive,
      isMenuOpen, setIsMenuOpen,
      consent, updateConsent,
      fullscreenGallery, setFullscreenGallery
    }}>
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => {
  const context = useContext(ContentContext);
  if (!context) throw new Error('useContent must be used within a ContentProvider');
  return context;
};

declare global {
  interface Window {
    dataLayer: any[];
  }
}